import { useUpdateAnswerToFirestore } from '@exam-room/graphql';
import { useDisclosure } from '@shared/hooks';
import { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { ExamLoadingScreen, FetchExamErrorModal } from '../../components';
import { useExamSession } from '../exam-session';

import { mapExamRoomPaperItemsToFormInput, mapFormInputToExamRoomPaperItemsInput } from './utils';

import { useNewExam } from '.';

interface NewExamFormProviderProps {
  children: React.ReactNode;
}

export interface ExamFormInput {
  [questionId: string]: {
    examPaperItemId: string;
    questionId: string;
    isBookmark: boolean;
    answer?: string;
  };
}

export interface NewExamFormContext {
  submitExam: () => Promise<void>;
}

const NewExamFormSubmitContext = createContext<NewExamFormContext | null>(null);

export const useNewExamFormOptional = () => {
  const submitContext = useContext(NewExamFormSubmitContext);
  return submitContext;
};

export const useNewExamForm = () => {
  const formContext = useFormContext<ExamFormInput>();
  const submitContext = useContext(NewExamFormSubmitContext);

  if (!submitContext) {
    throw new Error('useNewExamForm must be used within NewExamFormProvider');
  }

  return { ...formContext, ...submitContext };
};

function NewExamFormProvider({ children }: Readonly<NewExamFormProviderProps>) {
  const formContext = useForm<ExamFormInput>({
    mode: 'onTouched',
    defaultValues: {},
  });
  const {
    examSession,
    loading: isExamSessionLoading,
    error: examSessionError,
    refetch: refetchExamSession,
  } = useExamSession();
  const {
    cacheStatus,
    loading: isExamLoading,
    error: examError,
    refetch: refetchExam,
  } = useNewExam();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const [updateAnswer] = useUpdateAnswerToFirestore();

  useEffect(() => {
    if (!examSession) return;
    const formInput = mapExamRoomPaperItemsToFormInput(
      examSession?.examRoomAnswer?.examRoomExamPaperItems ?? null,
    );
    formContext.reset(formInput);
  }, [examSession, isExamSessionLoading, formContext]);

  useEffect(() => {
    if (!isRetrying && (examError || examSessionError)) {
      openModal();
    }
  }, [examSessionError, examError, isRetrying, openModal]);

  const handleSubmitExam = useCallback(
    async (data: ExamFormInput) => {
      const examRoomExamPaperItemsInput = mapFormInputToExamRoomPaperItemsInput(data);
      const updateAnswerData = await updateAnswer({
        variables: {
          examRoomExamPaperItems: examRoomExamPaperItemsInput,
        },
      });

      const defaultExamPaperId = examSession.examPaperId;
      const currentExamPaperId = updateAnswerData.data?.erExamRoomUpdateExamPaper.examPaperId;

      if (defaultExamPaperId !== currentExamPaperId) {
        // TODO: Navigate to session expired
        //   navigate(PATHS.SessionExpired);
      }
    },
    [examSession],
  );

  const submitExam = useCallback(async () => {
    await formContext.handleSubmit(handleSubmitExam)();
  }, [formContext.handleSubmit, handleSubmitExam]);

  const contextValue: NewExamFormContext = {
    submitExam,
  };

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    closeModal();
    try {
      await Promise.all([refetchExamSession(), refetchExam()]);
    } finally {
      setIsRetrying(false);
    }
  }, [refetchExamSession, refetchExam, closeModal]);

  if (isRetrying || isExamSessionLoading || cacheStatus.isCaching || isExamLoading) {
    return <ExamLoadingScreen />;
  }

  return (
    <>
      <FetchExamErrorModal open={modalOpened} onRetry={handleRetry} />
      <FormProvider {...formContext}>
        <NewExamFormSubmitContext.Provider value={contextValue}>
          <form
            noValidate
            onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
              e.stopPropagation();
              await formContext.handleSubmit(handleSubmitExam)(e);
            }}
            className='flex h-full flex-col'
            // onKeyDown={preventSubmission}
          >
            {children}
          </form>
        </NewExamFormSubmitContext.Provider>
      </FormProvider>
    </>
  );
}

export { NewExamFormProvider };
